// frontend/src/pages/employee/FieldVisits.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';

const FieldVisitManagement = () => {
  const { user } = useContext(AuthContext);
  const [fieldVisits, setFieldVisits] = useState([]);
  const [stations, setStations] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    station: '',
    dealer: '',
    categories: [],
    response: '',
    remarks: '',
    createdBy: user?.user?._id
  });

  useEffect(() => {
    fetchFieldVisits();
    fetchStations();
    fetchCategories();
  }, []);

  const fetchFieldVisits = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/field-visits`);
      const today = new Date().toISOString().split('T')[0];
      const filteredVisits = response.data.filter(visit => visit.createdAt.split('T')[0] === today);
      setFieldVisits(filteredVisits);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch field visits', 'error');
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  const fetchDealersByStation = async (stationId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/field-visits/dealers/${stationId}`);
      setDealers(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch dealers', 'error');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/field-visits/categories`);
      setCategories(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch categories', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'station') {
      fetchDealersByStation(value);
    }
  };

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    let updatedCategories = [...form.categories];
    if (checked) {
      updatedCategories.push(value);
    } else {
      updatedCategories = updatedCategories.filter(cat => cat !== value);
    }
    setForm({ ...form, categories: updatedCategories });
  };

  const handleSubmit = async () => {
    if (!form.station || !form.dealer || form.categories.length === 0 || !form.response || !form.remarks) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/field-visits`, form);
      Swal.fire('Success', 'Field visit recorded successfully', 'success');
      fetchFieldVisits();
      resetForm();
    } catch (error) {
      Swal.fire('Error', 'Failed to record field visit', 'error');
    }
  };

  const resetForm = () => {
    setForm({
      station: '',
      dealer: '',
      categories: [],
      response: '',
      remarks: '',
      createdBy: user?.user?._id
    });
  };

  return (
    <div className="p-4 md:p-6 bg-white shadow rounded-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Field Visit Management</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <select
          name="station"
          value={form.station}
          onChange={handleInputChange}
          className="w-full p-2 border rounded"
        >
          <option value="">Select Station</option>
          {stations.map(station => (
            <option key={station._id} value={station._id}>{station.name}</option>
          ))}
        </select>

        <select
          name="dealer"
          value={form.dealer}
          onChange={handleInputChange}
          className="w-full p-2 border rounded"
        >
          <option value="">Select Dealer</option>
          {dealers.map(dealer => (
            <option key={dealer._id} value={dealer._id}>{dealer.businessName}</option>
          ))}
        </select>

        <select
          name="response"
          value={form.response}
          onChange={handleInputChange}
          className="w-full p-2 border rounded"
        >
          <option value="">Select Response</option>
          <option value="Positive">Positive</option>
          <option value="Negative">Negative</option>
        </select>

        <div className="lg:col-span-3">
          <input
            type="text"
            name="remarks"
            value={form.remarks}
            onChange={handleInputChange}
            placeholder="Remarks"
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="space-y-2 lg:col-span-3">
          <p className="font-medium">Categories:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {categories.map(category => (
              <label key={category._id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={category._id}
                  checked={form.categories.includes(category._id)}
                  onChange={handleCategoryChange}
                  className="rounded"
                />
                <span>{category.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        className="w-full md:w-auto bg-blue-500 text-white py-2 rounded hover:bg-blue-600 px-4"
      >
        Record Visit
      </button>

      <div className="mt-8 space-y-6">
        <h3 className="text-xl font-bold text-gray-700">Today's Field Visits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fieldVisits.length === 0 && (
            <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <p className="text-gray-600">No field visits recorded today</p>
            </div>
          )}
          {fieldVisits.map((visit) => (
            <div key={visit._id} className="bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <p className="font-semibold text-lg text-gray-800">Station: {visit.station.name}</p>
              <p className="text-gray-600">Dealer: {visit.dealer.businessName}</p>
              <p className="text-gray-600">Categories: {visit.categories.length === 1 ? visit.categories[0].name : (visit.categories.map(cat => cat.name).slice(0, -1).join(', ') + ' and ' + visit.categories.slice(-1)[0].name)}</p>
              <p className={`font-medium ${visit.response === 'Positive' ? 'text-green-500' : 'text-red-500'}`}>Response: {visit.response}</p>
              <p className="text-gray-500 italic">Remarks: {visit.remarks}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldVisitManagement;
